import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CertFlow } from "./hub/CertFlow";
import { toast } from "sonner";
import { AlertTriangle, Clock, Database, DollarSign, Layers, Pause, Play, Trash2, Users, Video, XCircle } from "lucide-react";
import { api, API_BASE, errMsg, uploadSourceChunked } from "@/lib/api";
import { ExamFactsFields, ExamFactsEditor, emptyExamFacts, examFactErrors, examFactsPayload } from "./panel/ExamFacts";
import { CoveragePanel, EcoTree, EcoUploadPanel } from "./Wizard";
import { Card, Chip, QuietBtn, StatusPill, VIDEO_STATUS_COLORS } from "@/pages/admin/panel/ui";
import { PublishChecklistCard } from "@/pages/admin/panel/PublishChecklist";
import { LectureScriptEditor } from "@/pages/admin/panel/LectureScriptEditor";
import { VideoScriptEditor } from "@/pages/admin/panel/VideoScriptEditor";
import { MarketingTab } from "@/pages/admin/panel/MarketingTab";

const input = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400";
const TABS = ["Edit cert", "Review Queue", "Marketing", "Verification Log", "ECO Update"];

/* ---------------- Control Panel cards ---------------- */

export function VideoTargetEditor({ id, panel, onSaved }) {
  const c = panel.cert;
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(c.video_questions ?? 0);
  const [size, setSize] = useState(c.video_episode_size ?? 15);
  const save = async () => {
    try {
      await api.patch(`/admin/certs/${id}`, { exam_params: { video_questions: +target, video_episode_size: +size } });
      toast.success(`Video pool target ${+target} questions · ${+size} per episode — now run Repair bank to write the deficit`, { duration: 10000 });
      setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e), { duration: 10000 }); }
  };
  if (!open) {
    return <button data-testid="video-target-edit-btn" onClick={() => { setTarget(c.video_questions ?? 0); setSize(c.video_episode_size ?? 15); setOpen(true); }}
                   className="text-xs font-semibold text-sky-700 hover:underline">Set video pool target…</button>;
  }
  return (
    <div className="flex flex-wrap items-end gap-2 bg-sky-50 border border-sky-200 rounded-md p-2" data-testid="video-target-editor">
      <label className="text-[11px] font-medium text-zinc-600">Video pool (questions)
        <input data-testid="video-target-input" type="number" min="0" className="block w-28 border border-zinc-300 rounded-md px-2 py-1 text-sm bg-white" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
      <label className="text-[11px] font-medium text-zinc-600">Questions / episode
        <input data-testid="video-episode-size-input" type="number" min="1" className="block w-28 border border-zinc-300 rounded-md px-2 py-1 text-sm bg-white" value={size} onChange={(e) => setSize(e.target.value)} /></label>
      <button data-testid="video-target-save-btn" onClick={save} className="px-3 py-1.5 rounded-md bg-sky-500 text-white text-xs font-semibold hover:bg-sky-600">Save target</button>
      <QuietBtn data-testid="video-target-cancel-btn" onClick={() => setOpen(false)}>Cancel</QuietBtn>
    </div>
  );
}

export function UdemySpecCard({ id, spec, onChanged }) {
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/certs/${id}/udemy-spec/generate`);
      data.status === "pass" ? toast.success(`Udemy spec sheet generated — audit PASS (${data.audit?.description_words} word description)`)
                             : toast.error(`Udemy spec FAIL — ${data.failures.join("; ")}`, { duration: 12000 });
      onChanged();
    } catch (e) { toast.error(errMsg(e), { duration: 10000 }); }
    setBusy(false);
  };
  const pack = async () => {
    try {
      const r = await api.get(`/admin/certs/${id}/udemy-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `udemy-pack.zip` });
      a.click(); URL.revokeObjectURL(url);
      toast.success("Course pack downloaded — CSVs, intro MP4, spec sheet, course image");
    } catch (e) {
      const d = e?.response?.data;
      const msg = d instanceof Blob ? JSON.parse(await d.text()).detail : errMsg(e);
      toast.error(msg, { duration: 12000 });
    }
  };
  const ok = spec?.status === "pass";
  return (
    <div className={`rounded-md border px-4 py-3 ${!spec || ok ? "border-zinc-200 bg-white" : "border-red-300 bg-red-50"}`} data-testid="udemy-spec-card">
      <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Udemy course spec sheet</p>
        <p className="text-xs text-zinc-600" data-testid="udemy-spec-status">
          {!spec ? "Not generated yet — everything you paste into Udemy, in screen order."
            : ok ? `Audit PASS · ${spec.file_name} · ${new Date(spec.generated_at).toLocaleString()}`
            : `FAIL — ${spec.failures.join("; ")}`}
        </p>
      </div>
      {ok && <a data-testid="udemy-spec-download" href={`${API_BASE}/admin/certs/${id}/udemy-spec/spec`} className="text-xs font-semibold text-sky-700 hover:underline">Spec sheet (.txt)</a>}
      <QuietBtn data-testid="udemy-spec-generate-btn" disabled={busy} onClick={gen}>{busy ? "Generating…" : spec ? "Regenerate Udemy spec" : "Generate Udemy spec"}</QuietBtn>
      <button data-testid="udemy-pack-download-btn" onClick={pack} className="px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-700">Download course pack (.zip)</button>
      </div>
    </div>
  );
}

export function CourseImageCard({ id, image, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/certs/${id}/course-image`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Course image uploaded — ${file.name}`);
      onChanged();
    } catch (e) { toast.error(errMsg(e), { duration: 10000 }); }
    setBusy(false);
  };
  const legacy = image?.source === "generated";
  const src = image ? `${API_BASE}/admin/certs/${id}/course-image?v=${encodeURIComponent(image.uploaded_at || "")}` : null;
  return (
    <div className={`rounded-md border px-4 py-3 ${image && !legacy ? "border-zinc-200 bg-white" : "border-amber-300 bg-amber-50"}`} data-testid="course-image-card">
      <div className="flex flex-wrap items-start gap-4">
        {src && <img src={src} alt="Course image" data-testid="course-image-img" className="block rounded border border-zinc-200 shadow-sm bg-white"
                     style={{ width: legacy ? 250 : 120, height: "auto", aspectRatio: `${image.width || 752} / ${image.height || 1536}` }} />}
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Course image</p>
          <p className="text-xs text-zinc-700" data-testid="course-image-status">
            {!image ? "Not uploaded — the Udemy pack is blocked until you upload one (752 × 1536 PNG or JPG). Nothing is drawn by the machine."
              : legacy ? <><span className="inline-block rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-900 mr-1" data-testid="course-image-legacy-badge">auto-generated — replace</span>{image.name} ({image.width} × {image.height}) still ships in the pack until you upload yours.</>
              : <>{image.name} · {image.width} × {image.height} · uploaded {new Date(image.uploaded_at).toLocaleString()}. Shown at the top of the landing page buy box and shipped in the Udemy pack as-is.</>}
          </p>
          <label className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold cursor-pointer ${busy ? "bg-zinc-200 text-zinc-500" : "bg-sky-500 text-white hover:bg-sky-600"}`} data-testid="course-image-upload-label">
            {busy ? "Uploading…" : image ? "Replace course image" : "Upload course image"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={busy} data-testid="course-image-input"
                   onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
      </div>
    </div>
  );
}

function UdemyVideoRow({ id, which, label, verb, video, onChanged, disabledReason, edited }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const editable = which === "intro" || which === "promo";
  const ok = video && (video.status === "ready" || video.status === "rendered");
  const inFlight = video && ["scripted", "tts", "rendering"].includes(video.status);
  const render = async () => {
    if (video && !window.confirm(`Re-render the ${label}? The current MP4 is deleted and rebuilt with real narration.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/certs/${id}/udemy-video/${which}`, {});
      toast.success(`${label} queued — it renders on the video line; this row updates when it is done`);
      onChanged();
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  const tone = !video ? "border-amber-300 bg-amber-50" : ok ? "border-zinc-200 bg-white" : inFlight ? "border-sky-200 bg-sky-50" : "border-red-300 bg-red-50";
  const mins = video?.minutes ? `${video.minutes.toFixed(1)} min` : `${Math.round(video?.duration_seconds || 0)}s`;
  return (
    <div className="space-y-2">
    <div className={`rounded-md border px-4 py-3 flex flex-wrap items-center gap-3 ${tone}`} data-testid={`udemy-${which}-card`}>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}
          {edited && <span className="ml-2 inline-block rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800 normal-case tracking-normal" data-testid={`udemy-${which}-edited-badge`}>
            edited script{video && !video.script_edited ? " — re-render to apply" : ""}</span>}
        </p>
        {video ? (
          <>
            <p className="text-sm font-semibold text-zinc-900 truncate" data-testid={`udemy-${which}-title`}>{video.title}</p>
            <p className="text-xs text-zinc-600" data-testid={`udemy-${which}-status`}>
              {ok ? `${mins}${video.words ? ` · ${video.words} words` : ""} · audit ${String(video.audit || "").toUpperCase()}${video.transcript_checked ? " (transcript checked)" : ""} · ${video.file_name}`
                  : video.status === "failed" ? `FAILED — ${video.error}` : `${video.status}…`}
            </p>
            {video.notes?.length > 0 && <p className="text-xs text-amber-700" data-testid={`udemy-${which}-notes`}>{video.notes.join(" · ")}</p>}
            {video.audit_failures?.length > 0 && <p className="text-xs text-red-700">{video.audit_failures.join(" · ")}</p>}
          </>
        ) : (
          <p className="text-sm text-amber-900" data-testid={`udemy-${which}-status`}>{disabledReason || "Not rendered yet."}</p>
        )}
      </div>
      {ok && video.downloadable && <a data-testid={`udemy-${which}-download`} href={`${API_BASE}/admin/videos/${video.id}/download`} className="text-xs font-semibold text-sky-700 hover:underline">Download MP4</a>}
      {editable && <QuietBtn data-testid={`udemy-${which}-edit-script-btn`} disabled={inFlight} onClick={() => setEditing((v) => !v)}>{editing ? "Close editor" : "Edit script"}</QuietBtn>}
      <QuietBtn data-testid={`udemy-${which}-render-btn`} disabled={busy || inFlight} onClick={render}>
        {inFlight ? "Rendering…" : video ? `Re-render ${verb}` : `Render ${verb}`}
      </QuietBtn>
    </div>
    {editing && <LectureScriptEditor id={id} which={which} label={label} onClose={() => setEditing(false)} onSaved={onChanged} />}
    </div>
  );
}

export function UdemyVideosCard({ id, bank, onChanged }) {
  const walks = bank.udemy_walkthroughs || [];
  const tot = bank.udemy_walkthrough_total;
  return (
    <div className="space-y-2" data-testid="udemy-videos">
      <UdemyVideoRow id={id} which="intro" label="Udemy intro lecture (lecture 1)" verb="intro lecture" video={bank.udemy_intro} onChanged={onChanged} edited={!!bank.lecture_overrides?.intro}
                     disabledReason="Not rendered yet. Every exam fact must be filled first; the video pool cursor is untouched." />
      <UdemyVideoRow id={id} which="promo" label="Udemy course promo (90–120 s)" verb="course promo" video={bank.udemy_promo} onChanged={onChanged} edited={!!bank.lecture_overrides?.promo}
                     disabledReason="Not rendered yet. Why this course is for the learner — no price, no ExamRay, narration only." />
      <div className="rounded-md border border-zinc-200 bg-white px-4 py-3 space-y-2" data-testid="udemy-walkthroughs-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Udemy walkthroughs (Udemy needs 30 min of video)</p>
            <p className="text-xs text-zinc-600">YouTube episodes re-cut for Udemy, in order (1, 2, 3…) until the total clears 31 minutes: no ExamRay, no ticker, no offer; subtle examray.com watermark.</p>
            {tot && (
              <p className={`text-xs font-semibold mt-1 ${tot.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="udemy-walkthroughs-total">
                {tot.count} video{tot.count === 1 ? "" : "s"} · {Number(tot.total_minutes).toFixed(1)} min total · {tot.ok ? `clears the ${Math.round(tot.min_minutes)} min minimum` : `under the ${Math.round(tot.min_minutes)} min minimum — Render walkthroughs adds the next episode`}
              </p>
            )}
          </div>
          <WalkthroughsRenderBtn id={id} walks={walks} onChanged={onChanged} />
        </div>
        {(walks.length ? walks.map((_, i) => i + 1) : [1]).map((n) => {
          const w = walks[n - 1];
          const ok = w && (w.status === "ready" || w.status === "rendered");
          return (
            <div key={n} data-testid={`udemy-walkthrough-${n}`} className="flex flex-wrap items-center gap-3 text-xs border-t border-zinc-100 pt-2">
              <span className="font-semibold text-zinc-800 min-w-[9rem]">Walkthrough {n}</span>
              <span className="text-zinc-600 flex-1" data-testid={`udemy-walkthrough-${n}-status`}>
                {!w ? "not rendered" : ok ? `${(w.minutes || 0).toFixed(1)} min · audit ${String(w.audit || "").toUpperCase()}${w.transcript_checked ? " (transcript checked)" : ""} · ${w.file_name}`
                    : w.status === "failed" ? `FAILED — ${w.error}` : `${w.status}…`}
                {w?.notes?.length > 0 && <span className="text-amber-700"> · {w.notes.join(" · ")}</span>}
              </span>
              {ok && w.downloadable && <a data-testid={`udemy-walkthrough-${n}-download`} href={`${API_BASE}/admin/videos/${w.id}/download`} className="font-semibold text-sky-700 hover:underline">Download MP4</a>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WalkthroughsRenderBtn({ id, walks, onChanged }) {
  const [busy, setBusy] = useState(false);
  const any = walks.some(Boolean);
  const inFlight = walks.some((w) => w && ["scripted", "tts", "rendering"].includes(w.status));
  const render = async () => {
    if (any && !window.confirm("Re-render the walkthroughs? The current MP4s are deleted and the machine rebuilds as many episodes as it takes to clear 31 minutes (real narration).")) return;
    setBusy(true);
    try {
      await api.post(`/admin/certs/${id}/udemy-video/walkthroughs`, {});
      toast.success("Walkthroughs queued — they render on the video line");
      onChanged();
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  return <QuietBtn data-testid="udemy-walkthroughs-render-btn" disabled={busy || inFlight} onClick={render}>{inFlight ? "Rendering…" : any ? "Re-render walkthroughs" : "Render walkthroughs"}</QuietBtn>;
}

export function BankCard({ panel, id, onChanged }) {
  const [pickerFmt, setPickerFmt] = useState(null);
  const c = panel.cert;
  const scopes = pickerFmt === "udemy"
    ? [...Array.from({ length: c.udemy_exams }, (_, i) => ({ label: `Udemy exam ${i + 1}`, qs: `&bank=udemy&exam_number=${i + 1}` })),
       { label: "Udemy bank (all)", qs: "&bank=udemy" }]
    : [{ label: "Site bank (all paid exams)", qs: "&bank=paid" },
       ...Array.from({ length: c.num_paid_exams }, (_, i) => ({ label: `Paid exam ${i + 1}`, qs: `&bank=paid&exam_number=${i + 1}` })),
       { label: "Free-50", qs: "&bank=free50" }, { label: "Video pool", qs: "&bank=video" }];
  return (
    <Card icon={Database} title="Question bank" testid="panel-bank-card"
          actions={[["xlsx", "Excel"], ["csv", "CSV"], ["udemy", "Udemy"]].map(([fmt, label]) => (
            <div key={fmt} className="relative">
              <QuietBtn data-testid={`export-${fmt}-btn`}
                        onClick={() => setPickerFmt(pickerFmt === fmt ? null : fmt)}>{label}</QuietBtn>
              {pickerFmt === fmt && (
                <div data-testid="export-scope-picker"
                     className="absolute right-0 top-full mt-1 z-20 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 min-w-[220px] max-h-72 overflow-auto">
                  {scopes.map((s) => (
                    <a key={s.label} data-testid={`export-scope-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                       href={`${API_BASE}/admin/certs/${id}/export?fmt=${fmt}${s.qs}`}
                       onClick={() => setPickerFmt(null)}
                       className="block px-3 py-1.5 text-xs text-zinc-700 hover:bg-sky-50 hover:text-sky-700 transition-colors duration-150">
                      {s.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}>
      <div className="flex flex-wrap gap-3">
        <Chip label="Site bank" value={panel.bank.site.toLocaleString()} testid="chip-site-bank" />
        <Chip label="Free-50" value={panel.bank.free50} testid="chip-free50-bank" />
        <Chip label="Udemy" value={panel.bank.udemy} testid="chip-udemy-bank" />
        <Chip label="Video pool" value={`${panel.bank.video ?? 0} / ${panel.bank.video_target ?? 0}`} testid="chip-video-bank" />
        <Chip label="Flags" value={panel.bank.flags} testid="chip-flags" />
        <Chip label="Fix queue" value={panel.bank.fix_queue} testid="chip-fix-queue" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <VideoTargetEditor id={id} panel={panel} onSaved={onChanged} />
        {!(panel.bank.video_target > 0) && (
          <span className="text-xs text-amber-700" data-testid="video-target-zero-note">Video pool target is 0 — no video questions will be written until you set it.</span>
        )}
      </div>
      <p className="mt-3 text-xs text-zinc-400">Exports: full bank or a single exam — picker opens on click.</p>
    </Card>
  );
}

export function DebugToolsCard({ id }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const reassemble = async () => {
    if (!window.confirm("Re-assemble bank — retag every pool (paid / free-50 / Udemy / video) from the EXISTING approved questions. No model calls, no spend. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/certs/${id}/bank/reassemble`);
      const pools = Object.entries(data.sizes).map(([p, size]) => `${p} ${data.stored[p] ?? 0}/${size}`).join(", ");
      const tail = data.deficit
        ? ` — ${data.deficit} question(s) short (${Object.entries(data.short_by_pool).map(([p, n]) => `${p} ${n}`).join(", ")}); Repair bank writes exactly that deficit`
        : " — every pool full";
      toast.success(`Re-assembled: ${pools}${tail}`, { duration: 12000 });
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  const repair = async () => {
    if (!window.confirm("MANUAL OVERRIDE — the pipeline already runs this pass automatically at every BANK AUDIT entry. Run repair (question dedup) plus an incremental refill now anyway?")) return;
    try {
      const { data } = await api.post(`/admin/certs/${id}/bank/repair`, {});
      toast.success(`Manual repair started — ${data.duplicate_questions_removed} duplicate questions removed; incremental run queued`);
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
  };
  return (
    <div className="mt-2" data-testid="debug-tools">
      <div className="flex flex-wrap items-center gap-3">
        <QuietBtn data-testid="reassemble-bank-btn" disabled={!id || busy} onClick={reassemble}>{busy ? "Re-assembling…" : "Re-assemble bank (no model spend)"}</QuietBtn>
        <span className="text-[10px] text-zinc-400">Retags every pool from the approved questions already in the bank and reports the exact deficit. Run this first if the tiles disagree with the pool sizes.</span>
      </div>
      <button data-testid="debug-tools-toggle" onClick={() => setOpen(!open)}
              className="mt-2 text-[11px] font-semibold text-zinc-400 hover:text-zinc-600">
        {open ? "▾" : "▸"} Debug tools (manual overrides — the machine runs these itself)
      </button>
      {open && (
        <div className="mt-1 pl-4 border-l border-zinc-200">
          <QuietBtn data-testid="repair-bank-btn" disabled={!id} onClick={repair}>Repair bank (manual override)</QuietBtn>
          <p className="mt-1 text-[10px] text-zinc-400">Repair/dedup runs automatically as the entry to BANK AUDIT after writing and after every backfill cycle. Refused while the stored tags disagree with the pool sizes — press Re-assemble bank first.</p>
        </div>
      )}
    </div>
  );
}

export function BankAuditCard({ audit }) {
  const [showTable, setShowTable] = useState(false);
  if (!audit) return null;
  const pass = audit.status === "pass";
  const dist = audit.answer_distribution?.bank_wide;
  return (
    <Card icon={Layers} title="Bank audit" testid="panel-bank-audit-card"
          actions={<span data-testid="bank-audit-status"
                         className={`text-xs font-bold px-2 py-0.5 rounded-full border ${pass ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {pass ? "PASS" : "FAIL"}
          </span>}>
      {(audit.failures || []).map((f, i) => (
        <p key={i} data-testid={`bank-audit-failure-${i}`} className="text-xs text-red-700 font-medium">✗ {f}</p>
      ))}
      {(audit.review || []).map((r, i) => (
        <p key={i} data-testid={`bank-audit-review-${i}`} className="text-xs text-amber-700">⚠ {r}</p>
      ))}
      {(audit.halt || []).length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid="bank-audit-halt-reasons">
          {audit.halt.map((h, i) => (
            <p key={i} className="text-xs font-mono text-red-700">
              halted: objective {h.objective_id}{h.missing > 0 ? ` short ${h.missing}` : ""} — {h.reason}
            </p>
          ))}
        </div>
      )}
      {(audit.structural || []).length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid="bank-audit-structural">
          {audit.structural.map((s, i) => (
            <p key={i} className="text-[11px] text-amber-700">⚠ {s.objective_id}: {s.reason}</p>
          ))}
        </div>
      )}
      {(audit.setup?.warnings || []).map((w, i) => (
        <p key={i} data-testid={`bank-audit-setup-warning-${i}`} className="mt-1 text-[11px] text-amber-700">⚠ setup: {w}</p>
      ))}
      {audit.loop && (
        <p className="mt-2 text-xs text-zinc-600" data-testid="bank-audit-loop">
          <span className="font-semibold">Self-complete loop:</span> {audit.loop.cycles_used} cycle{audit.loop.cycles_used === 1 ? "" : "s"} · dups removed {audit.loop.dups_removed_questions}q · backfilled {Object.entries(audit.loop.backfill_per_objective || {}).map(([k, v]) => `${k}+${v}`).join(", ") || "0"} · retired {Object.entries(audit.loop.retired_per_objective || {}).map(([k, v]) => `${k}−${v}`).join(", ") || "0"} · flags replaced {audit.loop.flags_replaced ?? 0} · progress-gated{typeof audit.loop.stall === "number" ? ` (stall ${audit.loop.stall}/${audit.loop.stall_limit ?? 2})` : ""}
        </p>
      )}
      {(audit.pool_tables || []).length > 0 && (
        <div className="mt-1 space-y-0.5" data-testid="bank-audit-pool-tables">
          {audit.pool_tables.map((pt) => (
            <p key={pt.pool} data-testid={`pool-table-${pt.pool}`}
               className={`text-[11px] font-mono ${pt.table_sum === pt.size ? "text-zinc-500" : "text-red-700 font-bold"}`}>
              pool {pt.pool}: table sums to {pt.table_sum} / pool size {pt.size} {pt.table_sum === pt.size ? "✓" : "✗ SETUP ERROR"}
              {" · "}{pt.rows.filter((r) => r.delta < 0).length ? `${pt.rows.filter((r) => r.delta < 0).length} objective(s) short` : "coverage complete"}
            </p>
          ))}
        </div>
      )}
      {audit.loop?.write_stats && Object.keys(audit.loop.write_stats).length > 0 && (
        <p className="mt-1 text-[11px] font-mono text-zinc-500" data-testid="bank-audit-write-stats">
          refill: {Object.entries(audit.loop.write_stats).map(([k, v]) => `${k} req ${v.requested}/banked ${v.banked}/deduped ${v.deduped}/rejected ${v.rejected}`).slice(0, 8).join(" · ")}
        </p>
      )}
      {(audit.domain_table || []).length > 0 && (
        <p className="mt-1 text-[11px] font-mono text-zinc-500" data-testid="bank-audit-domains">
          {audit.domain_table.map((d) => `${d.domain}: ${d.banked}/${d.target} (${d.weight_pct}%)`).join(" · ")}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-3">
        <Chip label="Questions" value={audit.questions ?? 0} testid="chip-audit-questions" />
        {dist && <Chip label="Answer keys" value={Object.entries(dist).map(([k, v]) => `${k}=${v}`).join(" ")} testid="chip-audit-keys" />}
      </div>
      <button data-testid="bank-audit-table-toggle" onClick={() => setShowTable(!showTable)}
              className="mt-2 text-xs font-semibold text-sky-600 hover:underline">
        {showTable ? "Hide" : "Show"} per-objective count table ({(audit.objective_table || []).length} objectives)
      </button>
      {showTable && (
        <div className="mt-2 max-h-72 overflow-auto border border-zinc-200 rounded-md" data-testid="bank-audit-objective-table">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-zinc-50 sticky top-0">
              <tr className="text-left text-zinc-500">
                <th className="px-2 py-1">Objective</th><th className="px-2 py-1">Domain</th>
                <th className="px-2 py-1 text-right">Target</th><th className="px-2 py-1 text-right">Banked</th>
                <th className="px-2 py-1 text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {(audit.objective_table || []).map((r) => (
                <tr key={r.objective_id} data-testid={`audit-row-${r.objective_id}`}
                    className={r.delta !== 0 ? "bg-red-50 text-red-700" : ""}>
                  <td className="px-2 py-0.5">{r.objective_id}</td>
                  <td className="px-2 py-0.5 truncate max-w-[220px]">{r.domain}</td>
                  <td className="px-2 py-0.5 text-right">{r.target}</td>
                  <td className="px-2 py-0.5 text-right">{r.banked}</td>
                  <td className="px-2 py-0.5 text-right font-bold">{r.delta > 0 ? `+${r.delta}` : r.delta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(audit.duplicates?.same_exam || []).length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid="bank-audit-duplicates">
          {audit.duplicates.same_exam.map((d, i) => (
            <p key={i} className="text-[11px] font-mono text-red-700">dup ×{d.count} [{(d.placements || []).join(", ")}]: “{d.stem}”</p>
          ))}
        </div>
      )}
      <p className="mt-2 text-[10px] text-zinc-400">Audited {audit.at ? new Date(audit.at).toLocaleString() : "—"} · blueprint coverage, same-exam duplicates, answer-key balance, persona variety, pool overlap.</p>
    </Card>
  );
}

function indexNowChip(ix) {
  if (!ix) return { value: "not pushed yet", cls: "text-zinc-500" };
  const when = ix.at ? new Date(ix.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  if (ix.status === "ok") return { value: `IndexNow ✓ ${(ix.engines || []).length} engines · ${when}`, cls: "text-emerald-700" };
  if (ix.status === "skipped") return { value: `IndexNow skipped · ${ix.reason}`, cls: "text-amber-700" };
  return { value: `IndexNow ✗ ${ix.error || "push rejected"} · ${when}`, cls: "text-red-600" };
}

export function SalesCard({ panel, certId, onChanged }) {
  const s = panel.sales;
  const ix = indexNowChip(panel.indexnow);
  const repush = async () => {
    try { const { data } = await api.post(`/admin/certs/${certId}/indexnow`); toast[data.status === "ok" ? "success" : data.status === "skipped" ? "info" : "error"](data.status === "ok" ? `IndexNow accepted (${data.code})` : data.error || data.reason); onChanged && onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card icon={Users} title="Sales and users" testid="panel-sales-card"
          actions={<span className="text-xs text-zinc-400">Last 30 days · this cert</span>}>
      <div className="flex flex-wrap gap-3 mb-3 items-center" data-testid="search-engine-row">
        <div className="min-w-[180px] flex-1 border border-zinc-200 rounded-md px-3 py-2 bg-white" data-testid="chip-indexnow">
          <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-400">Search engines</p>
          <p className={`text-sm font-semibold ${ix.cls}`}>{ix.value}</p>
          {panel.indexnow?.key_location && (
            <p className="text-[11px] text-zinc-500 mt-0.5 font-mono break-all" data-testid="indexnow-detail">
              host {panel.indexnow.host} · key file <a href={panel.indexnow.key_location} target="_blank" rel="noreferrer" className="underline">{panel.indexnow.key_location}</a>
              {panel.indexnow.selfcheck && ` · self-check HTTP ${panel.indexnow.selfcheck.code}${panel.indexnow.selfcheck.matches ? " ✓ key matches" : " ✗ body differs"}`}
            </p>
          )}
          <p className="text-[11px] text-zinc-400 mt-0.5">Google: discovers via /api/sitemap.xml in robots.txt (no ping exists)</p>
        </div>
        {panel.cert.status === "published" && <QuietBtn data-testid="indexnow-repush-btn" onClick={repush}>Re-push to IndexNow</QuietBtn>}
      </div>
      <div className="flex flex-wrap gap-3">
        <Chip label="Visitors" value={s.visitors.toLocaleString()} testid="chip-visitors" />
        <Chip label="Free-50 takers" value={s.free50.toLocaleString()} testid="chip-free50" />
        <Chip label="Paid users" value={s.paid.toLocaleString()} testid="chip-paid" />
        <Chip label="Revenue" value={`$${s.revenue.toLocaleString()}`} testid="chip-revenue" />
        <Chip label="Refunds" value={s.refunds} testid="chip-refunds" />
        {(s.codes || []).map((c) => (
          <Chip key={c.code} label={`Code ${c.code}`} value={`×${c.total} · ${c.examray} site / ${c.stripe_promo} Stripe`} testid={`chip-code-${c.code}`} />
        ))}
      </div>
      <p className="mt-3 text-sm text-zinc-600" data-testid="funnel-line">{s.funnel}</p>
    </Card>
  );
}

export function PipelineCard({ panel, onFix }) {
  const p = panel.pipeline;
  const id = panel.cert.id || panel.cert._id;
  const smoke = panel.cert.smoke_result;
  const [smokeBusy, setSmokeBusy] = useState(false);
  const runSmoke = async () => {
    setSmokeBusy(true);
    try {
      await api.post(`/admin/certs/${panel.cert.id}/smoke-run`, {});
      toast.success("Test run started — 5 questions, $2 cap, auto-cleaned");
    } catch (e) { toast.error(errMsg(e)); }
    setSmokeBusy(false);
  };
  const regenerate = async () => {
    const typed = window.prompt(`Regenerate in place: clears the question bank and derived artifacts, then reruns against the CURRENT ECO, sources and configuration. Deleting the cert is NOT required.\nType the cert slug (${panel.cert.slug}) to confirm:`);
    if (typed == null) return;
    try {
      await api.post(`/admin/certs/${panel.cert.id}/regenerate?confirm=${encodeURIComponent(typed)}`, {});
      toast.success("Regenerating in place — bank cleared, fresh run started");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (p.idle) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [p.idle]);
  const started = p.started_at ? new Date(p.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  const hbSec = p.heartbeat ? Math.max(0, Math.floor((nowMs - new Date(p.heartbeat).getTime()) / 1000)) : null;
  const hbAge = hbSec == null ? null
    : hbSec < 60 ? `${hbSec}s ago`
    : hbSec < 3600 ? `${Math.floor(hbSec / 60)}m ${hbSec % 60}s ago`
    : `${Math.floor(hbSec / 3600)}h ${Math.floor((hbSec % 3600) / 60)}m ago`;
  const hbTone = hbSec == null ? "" : hbSec >= 600 ? "bg-red-50 text-red-700 border-red-200"
    : hbSec >= 90 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const chip = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium";
  return (
    <Card icon={Clock} title="Pipeline" testid="panel-pipeline-card"
          actions={
            <span className="flex items-center gap-2">
              {!p.idle && (
                <span className="text-xs text-zinc-400">
                  {started && `Started ${started}`}{p.eta_minutes != null && ` · est. ${Math.ceil(p.eta_minutes)} min left`}
                </span>
              )}
              <QuietBtn data-testid="smoke-run-btn" disabled={smokeBusy || smoke?.status === "running"}
                        onClick={runSmoke}>
                {smoke?.status === "running" ? "Test running…" : "Test run ($2 cap)"}
              </QuietBtn>
              <QuietBtn data-testid="regenerate-btn" onClick={regenerate}>Regenerate</QuietBtn>
            </span>
          }>
      <p className={`text-sm ${p.sentence?.startsWith("HALTED") ? "text-red-700 font-medium" : "text-zinc-700"}`} data-testid="pipeline-sentence">{p.sentence}</p>
      {(p.label || !p.idle) && (
        <p className="text-[11px] text-zinc-400 mt-0.5" data-testid="pipeline-job-label">
          {[
            p.label?.type, p.label?.cert_name, p.label?.app_version,
            p.started_at && `started ${new Date(p.started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`,
            p.finished_at && `finished ${new Date(p.finished_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`,
          ].filter(Boolean).join(" · ")}
        </p>
      )}
      {!p.idle && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="pipeline-timeline">
          {p.questions_total != null && (
            <span data-testid="timeline-questions" className={`${chip} bg-sky-50 text-sky-700 border-sky-200`}>
              Questions {p.questions_written ?? 0} / ~{p.questions_total}
            </span>
          )}
          {hbAge && (
            <span data-testid="timeline-heartbeat" className={`${chip} ${hbTone}`}>
              {hbSec >= 600 ? "STALLED — last activity" : hbSec >= 90 ? "Possible stall — last activity" : "Last activity"} {hbAge}
            </span>
          )}
          {p.run_spend_usd != null && (
            <span data-testid="pipeline-run-spend" className={`${chip} bg-zinc-50 text-zinc-600 border-zinc-200`}>
              Spend ${p.run_spend_usd.toFixed(2)}
            </span>
          )}
        </div>
      )}
      {smoke && smoke.status !== "running" && smoke.stages && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="smoke-result">
          <span className={`text-[11px] font-bold ${smoke.status === "pass" ? "text-emerald-700" : "text-red-700"}`}>
            Test run: {smoke.status.toUpperCase()}{smoke.spend_usd != null && ` · $${smoke.spend_usd.toFixed(2)}`}
          </span>
          {Object.entries(smoke.stages).map(([st, res]) => (
            <span key={st} data-testid={`smoke-stage-${st}`}
                  className={`${chip} ${res === "pass" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : res === "fail" ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-zinc-50 text-zinc-400 border-zinc-200"}`}>
              {st === "originality" ? "similarity" : st} {res === "pass" ? "✓" : res === "fail" ? "✗" : "—"}
            </span>
          ))}
          {smoke.error && <span className="text-[11px] text-red-600 w-full">{smoke.error}</span>}
          {smoke.fault_injection && (
            <span data-testid="smoke-fault-injection" className={`${chip} bg-violet-50 text-violet-700 border-violet-200`}>
              faults: empty item skipped {smoke.fault_injection.empty_item_skipped ? "✓" : "✗"} · errored item skipped {smoke.fault_injection.errored_item_skipped ? "✓" : "✗"} · duplicate poll ignored {smoke.fault_injection.duplicate_poll_ignored ? "✓" : "✗"} · refusal probe routed {smoke.fault_injection.refusal_probe ? "✓" : "✗"} · bank-audit defect probe {smoke.fault_injection.bank_audit_defect_probe ? "✓" : "✗"} · self-complete loop {smoke.fault_injection.self_complete_loop ? "✓" : "✗"}
            </span>
          )}
          {smoke.shortfall_report?.length > 0 && (
            <div className="w-full mt-1" data-testid="smoke-shortfall-report">
              {smoke.shortfall_report.map((s, i) => (
                <p key={i} className="text-[11px] text-amber-700">shortfall: objective {s.objective_id} — {s.short} of {s.target} short ({s.reason})</p>
              ))}
            </div>
          )}
          {smoke.verdicts?.length > 0 && (
            <div className="w-full mt-1 space-y-0.5" data-testid="smoke-verdicts">
              {smoke.verdicts.map((v, i) => (
                <p key={i} className="text-[11px] text-zinc-500">
                  <span className={`font-semibold ${v.verdict === "pass" ? "text-emerald-700" : "text-red-700"}`}>{v.verdict}</span>
                  {" — "}{v.reason} · “{v.question}”
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {p.failed_item && (
        <div className="mt-2 text-[11px] font-mono bg-red-50 border border-red-200 rounded p-2 text-red-700" data-testid="pipeline-failed-item">
          <p>Failing item: batch {p.failed_item.batch_id} · {p.failed_item.custom_id} · result {p.failed_item.result_type} · stop_reason {String(p.failed_item.stop_reason)}</p>
          {p.failed_item.request_params && (
            <p className="text-zinc-600 break-all mt-0.5">request: {JSON.stringify(p.failed_item.request_params)}</p>
          )}
        </div>
      )}
      {p.explain && (
        <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2.5" data-testid="pipeline-explain">
          <p className="text-xs text-zinc-700 leading-relaxed">{p.explain}</p>
        </div>
      )}
      {p.sentence?.startsWith("HALTED") && (
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <button data-testid="pipeline-restart-btn" disabled={!p.job_id}
                  onClick={async () => {
                    if (!p.job_id) return;
                    try {
                      await api.post(`/admin/jobs/${p.job_id}/restart`);
                      toast.success("Job requeued — resuming from banked work");
                    } catch (e) { toast.error(errMsg(e)); }
                  }}
                  className="px-3 py-1.5 rounded-md bg-sky-500 text-white text-xs font-semibold hover:bg-sky-600 transition-colors duration-150 disabled:opacity-50">
            Restart run
          </button>
          {p.accept_offer && (
            <button data-testid="pipeline-accept-risk-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/admin/certs/${id}/accept-risk`, {});
                        toast.success(data.message);
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                    className="px-3 py-1.5 rounded-md bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-colors duration-150">
              Accept single-source risk & finish
            </button>
          )}
          <button data-testid="pipeline-fix-link" onClick={onFix}
                  className="text-xs font-semibold text-sky-600 hover:underline">Fix cert setup, sources & ECO →</button>
        </div>
      )}
      {!p.idle && (
        <div className="mt-3 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-sky-500 transition-all duration-300" style={{ width: `${p.progress}%` }} />
        </div>
      )}
    </Card>
  );
}

export function JobHistoryCard({ id, openJob = null }) {
  const [open, setOpen] = useState(Boolean(openJob));
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(openJob);
  useEffect(() => {
    api.get(`/admin/certs/${id}/jobs/history`).then((r) => setRows(r.data.jobs)).catch(() => {});
    const t = setInterval(() => {
      api.get(`/admin/certs/${id}/jobs/history`).then((r) => setRows(r.data.jobs)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [id]);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
  const OUTCOME = { done: "text-emerald-700", failed: "text-red-700", cancelled: "text-zinc-500" };
  const restart = async (jid) => {
    try { await api.post(`/admin/jobs/${jid}/restart`); toast.success("Job requeued"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl" data-testid="job-history-section">
      <button data-testid="job-history-toggle" onClick={() => setOpen(!open)}
              className="w-full px-5 py-3 flex items-center justify-between text-sm font-bold hover:bg-zinc-50 transition-colors duration-150 rounded-xl">
        <span>Job History <span className="text-zinc-400 font-normal">({rows.length})</span></span>
        <span className="text-zinc-400">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="divide-y divide-zinc-100 border-t border-zinc-100">
          {rows.map((j) => (
            <div key={j.id} className="px-5 py-2.5 text-xs" data-testid={`job-history-row-${j.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`font-bold ${OUTCOME[j.status] || ""}`}>{j.status}</span>
                <span className="font-semibold">{[j.label?.type || j.type, j.label?.app_version].filter(Boolean).join(" · ")}</span>
                <span className="text-zinc-400">started {fmt(j.started_at || j.created_at)} · finished {fmt(j.finished_at)}</span>
                {j.questions_written != null && <span>banked {j.questions_written}{j.questions_total != null && `/${j.questions_total}`}</span>}
                {j.requested != null && <span data-testid={`job-history-attempts-${j.id}`} className="text-zinc-500">requested {j.requested} · discarded {j.discarded ?? 0}</span>}
                {j.run_spend_usd != null && <span>${j.run_spend_usd.toFixed(2)}</span>}
                <span className="ml-auto flex items-center gap-2">
                  {j.status === "failed" && (
                    <button data-testid={`history-restart-${j.id}`} onClick={() => restart(j.id)}
                            className="text-sky-600 hover:underline">restart</button>
                  )}
                  {(j.failed_item || j.skipped_items?.length > 0 || j.shortfall_report?.length > 0) && (
                    <button data-testid={`job-history-detail-btn-${j.id}`} onClick={() => setDetail(detail === j.id ? null : j.id)}
                            className="text-sky-600 hover:underline">diagnostics</button>
                  )}
                </span>
              </div>
              {j.error && <p className="text-red-600 mt-0.5">{j.error}</p>}
              {detail === j.id && (
                <div className="mt-1.5 font-mono text-[11px] bg-zinc-50 border border-zinc-200 rounded p-2 space-y-1" data-testid={`job-history-diagnostics-${j.id}`}>
                  {j.failed_item && (
                    <p className="text-red-700">failing item: batch {j.failed_item.batch_id} · {j.failed_item.custom_id} · {j.failed_item.result_type} · stop_reason {String(j.failed_item.stop_reason)}
                      {j.failed_item.request_params && ` · ${JSON.stringify(j.failed_item.request_params)}`}</p>
                  )}
                  {(j.skipped_items || []).map((s, i) => (
                    <p key={i} className="text-zinc-600">skipped: {s.batch_id} · {s.custom_id} · {s.result_type} · stop_reason {String(s.stop_reason)}{s.request_params && ` · ${JSON.stringify(s.request_params)}`}</p>
                  ))}
                  {(j.shortfall_report || []).map((s, i) => (
                    <p key={i} className="text-amber-700">shortfall: objective {s.objective_id} — {s.short} of {s.target} short ({s.reason})</p>
                  ))}
                  {j.bank_report?.questions_per_model && (
                    <p className="text-zinc-600">per model: {JSON.stringify(j.bank_report.questions_per_model)}
                      {j.bank_report.refusals_per_objective && Object.keys(j.bank_report.refusals_per_objective).length > 0 && ` · refusals: ${JSON.stringify(j.bank_report.refusals_per_objective)}`}</p>
                  )}
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && <p className="px-5 py-4 text-zinc-400 text-xs">No finished jobs yet.</p>}
        </div>
      )}
    </div>
  );
}

export function VideoLineCard({ panel, id, refresh }) {
  const [videos, setVideos] = useState([]);
  const [ttsCfg, setTtsCfg] = useState(null);
  const [allowMock, setAllowMock] = useState(false);
  const [ticker, setTicker] = useState(panel.cert.ticker_url);
  const [smoke, setSmoke] = useState(null);
  const paused = panel.cert.video_paused;

  const load = useCallback(() => {
    api.get(`/admin/certs/${id}/videos`).then((r) => setVideos(r.data.videos)).catch(() => {});
    api.get("/admin/config/tts").then((r) => setTtsCfg(r.data)).catch(() => {});
    api.get(`/admin/certs/${id}/render-smoke`).then((r) => setSmoke(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const pauseQ = () => act(() => api.post(`/admin/certs/${id}/videos/${paused ? "resume" : "pause"}`), paused ? "Queue resumed" : "Queue paused — takes effect before the next video");
  const cancelQ = () => act(() => api.post(`/admin/certs/${id}/videos/cancel`), "Queue cancelled — in-flight render may finish");
  const startVideos = () => act(() => api.post(`/admin/certs/${id}/videos`, { allow_mock_tts: allowMock }), "Video render job queued — next block of episodes");
  const clearVideos = () => {
    if (!window.confirm("Delete EVERY rendered video and chapter file for this cert (Spaces objects purged) and restart the episode series from question 1?")) return;
    act(() => api.post(`/admin/certs/${id}/videos/clear`), "All rendered videos deleted — series reset");
  };
  const vc = panel.cert;
  const poolLeft = Math.max(0, (panel.bank.video ?? 0) - (vc.video_cursor ?? 0));
  const epSize = vc.video_episode_size || 15;
  const nextEps = Math.floor(poolLeft / epSize);
  const saveTicker = () => act(() => api.put(`/admin/certs/${id}/ticker`, { ticker_url: ticker }), "Ticker URL saved — used in every render for this cert");
  const saveTts = (provider, voice) => act(() => api.put("/admin/config/tts", { provider, voice }), `Brand voice locked: ${provider} / ${voice}`);
  const renderSamples = () => act(() => api.post("/admin/voice-samples/render"), "Voice comparison samples rendered");

  const [editingId, setEditingId] = useState(null);
  const editable = (v) => !["tts", "rendering"].includes(v.status);  // v1.25.0: every row — episodes, shorts, walkthroughs, intro, promo, compilation
  const LECTURE_WHICH = { udemy_intro: "intro", udemy_promo: "promo" };
  const editorFor = (v) => {
    const close = () => setEditingId(null), saved = () => { load(); refresh(); };
    if (LECTURE_WHICH[v.kind]) return <div className="mx-1 mb-2"><LectureScriptEditor id={id} which={LECTURE_WHICH[v.kind]} label={v.title} onClose={close} onSaved={saved} /></div>;
    if (v.kind === "compilation")
      return <p className="mx-1 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`video-editor-compilation-${v.id}`}>
        This is the episodes joined into one file — it has no script of its own. Edit the episode you want to change, Re-render that episode, then Re-render this Full Set.</p>;
    return <VideoScriptEditor video={v} onClose={close} onSaved={saved} />;
  };
  const [rowMsg, setRowMsg] = useState({});
  const rerender = async (v) => {
    if (!window.confirm(`Re-render "${v.title}" from its current script? The existing MP4 and chapters are purged first.`)) return;
    setRowMsg((m) => ({ ...m, [v.id]: { ok: true, text: "Queuing re-render…" } }));
    try {
      const { data } = await api.post(`/admin/videos/${v.id}/rerender`, { allow_mock_tts: allowMock });
      setRowMsg((m) => ({ ...m, [v.id]: { ok: true, text: data.note } }));
      toast.success(data.note, { duration: 8000 });
      load(); refresh();
    } catch (e) {
      const msg = errMsg(e);
      setRowMsg((m) => ({ ...m, [v.id]: { ok: false, text: `Re-render refused: ${msg}` } }));
      toast.error(`Re-render refused: ${msg}`, { duration: 15000 });
    }
  };
  const deleteOne = (v) => {
    if (!window.confirm(`Delete "${v.title}" — MP4 and chapters purged from Spaces? (The episode series continues; use "Delete all videos" to restart it.)`)) return;
    act(() => api.delete(`/admin/videos/${v.id}`), "Video deleted");
  };
  const rowAction = (v) => {
    if (["scripted", "tts", "rendering"].includes(v.status))
      return <QuietBtn danger data-testid={`video-cancel-${v.id}`} onClick={() => act(() => api.post(`/admin/videos/${v.id}/cancel`), "Video cancelled")}>Cancel</QuietBtn>;
    const del = <button data-testid={`video-delete-${v.id}`} onClick={() => deleteOne(v)} className="text-xs text-red-600 hover:underline">Delete</button>;
    if (v.downloadable && ["rendered", "ready"].includes(v.status))
      return (
        <span className="flex items-center gap-2">
          {editable(v) && <button data-testid={`video-edit-${v.id}`} onClick={() => setEditingId(editingId === v.id ? null : v.id)} className="text-xs text-sky-600 hover:underline">{editingId === v.id ? "Close" : "Edit"}</button>}
          <button data-testid={`video-rerender-${v.id}`} onClick={() => rerender(v)} className={`text-xs hover:underline ${v.render_stale ? "font-semibold text-amber-700" : "text-zinc-600"}`}>Re-render</button>
          <a data-testid={`video-download-${v.id}`} href={`${API_BASE}/admin/videos/${v.id}/download`} className="text-xs text-sky-600 hover:underline">Download</a>
          {del}
        </span>
      );
    if (["failed", "cancelled"].includes(v.status))
      return (
        <span className="flex items-center gap-2">
          <button data-testid={`video-edit-${v.id}`} onClick={() => setEditingId(editingId === v.id ? null : v.id)} className="text-xs text-sky-600 hover:underline">{editingId === v.id ? "Close" : "Edit"}</button>
          <QuietBtn data-testid={`video-retry-${v.id}`} onClick={() => act(() => api.post(`/admin/videos/${v.id}/retry`, { allow_mock_tts: allowMock }), "Retry queued")}>Retry</QuietBtn>
          {del}
        </span>
      );
    return del;
  };

  return (
    <Card icon={Video} title="Video line" testid="panel-video-card"
          actions={<>
            {videos.length > 0 && <QuietBtn danger data-testid="clear-videos-header-btn" onClick={clearVideos}><Trash2 size={12} /> Delete all videos ({videos.length})</QuietBtn>}
            <QuietBtn data-testid="pause-queue-btn" onClick={pauseQ}>
              {paused ? <><Play size={12} /> Resume queue</> : <><Pause size={12} /> Pause queue</>}
            </QuietBtn>
            <QuietBtn danger data-testid="cancel-queue-btn" onClick={cancelQ}><XCircle size={12} /> Cancel queue</QuietBtn>
          </>}>
      {paused && <p className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="queue-paused-note">Queue paused — nothing renders until you resume.</p>}
      <div className="flex flex-wrap gap-3 justify-center">
        <Chip label="Queued" value={panel.videos.queued} testid="chip-vq-queued" />
        <Chip label="Rendering" value={panel.videos.rendering} testid="chip-vq-rendering" />
        <Chip label="Done" value={panel.videos.done} testid="chip-vq-done" />
        <Chip label="Failed" value={panel.videos.failed} testid="chip-vq-failed" />
      </div>

      <div className={`mt-4 ${editingId ? "" : "max-h-72"} overflow-auto border-t border-zinc-100`} data-testid="video-table">
        {videos.map((v) => (
          <div key={v.id} className="border-b border-zinc-100" data-testid={`video-row-${v.id}`}>
          <div className="flex items-center gap-3 py-2 text-sm">
            <span className="flex-1 truncate">{v.title}
              {v.render_stale && <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`video-stale-${v.id}`}>edited — re-render to apply</span>}
              {v.script_edited && !v.render_stale && <span className="ml-2 inline-block rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800" data-testid={`video-edited-${v.id}`}>edited script</span>}
              {LECTURE_WHICH[v.kind] && panel.bank.lecture_overrides?.[LECTURE_WHICH[v.kind]] && !v.script_edited && <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`video-stale-${v.id}`}>edited — re-render to apply</span>}
            </span>
            <span className={`text-xs font-medium capitalize ${VIDEO_STATUS_COLORS[v.status] || "text-zinc-500"}`}>{v.status === "tts" ? "queued" : v.status === "scripted" ? "queued" : v.status}</span>
            <span className="shrink-0 flex justify-end min-w-[12rem]">{rowAction(v)}</span>
          </div>
          {rowMsg[v.id] && <p className={`mx-1 mb-2 rounded px-2 py-1 text-xs ${rowMsg[v.id].ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700 font-semibold"}`} data-testid={`video-rowmsg-${v.id}`}>{rowMsg[v.id].text}</p>}
          {editingId === v.id && editorFor(v)}
          </div>
        ))}
        {videos.length === 0 && <p className="py-6 text-center text-zinc-400 text-sm">No videos yet.</p>}
      </div>

      <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="render-smoke-panel">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs font-bold text-zinc-700">Render smoke</p>
          {smoke?.exists ? (
            <span data-testid="render-smoke-verdict"
                  className={`text-[11px] font-bold px-2 py-0.5 rounded ${smoke.verdict === "pass" ? "bg-emerald-100 text-emerald-700" : smoke.status === "failed" || smoke.verdict === "fail" ? "bg-red-100 text-red-700" : "bg-zinc-200 text-zinc-600"}`}>
              {smoke.verdict ? smoke.verdict.toUpperCase() : smoke.status}
            </span>
          ) : <span className="text-[11px] text-zinc-500">not yet run</span>}
          <QuietBtn data-testid="run-render-smoke-btn"
                    onClick={() => act(() => api.post(`/admin/certs/${id}/videos/smoke`, { allow_mock_tts: allowMock }),
                                       "Render smoke queued — one short episode, one short, one compilation")}>
            Run render smoke
          </QuietBtn>
        </div>
        <p className="mt-1 text-[10px] text-zinc-500">A green smoke (real TTS, real FFmpeg, cost-capped) is required before any full batch — every cert, machine-wide.</p>
        {(smoke?.chips || []).map((c, i) => (
          <p key={i} data-testid={`render-smoke-chip-${c.name}`} className={`mt-1 text-[11px] ${c.ok ? "text-emerald-700" : "text-red-700"}`}>
            {c.ok ? "✓" : "✗"} {c.name} — {c.detail}
          </p>
        ))}
        {smoke?.error && <p className="mt-1 text-[11px] text-red-700" data-testid="render-smoke-error">{smoke.error}</p>}
      </div>

      <p className="mt-4 text-xs text-zinc-600" data-testid="video-pool-status">
        Video pool: <b>{panel.bank.video ?? 0}</b> of {panel.bank.video_target ?? 0} questions generated · <b>{vc.video_cursor ?? 0}</b> used in {vc.video_episodes_done ?? 0} episode(s) · <b>{poolLeft}</b> unused → next run renders <b>{nextEps}</b> episode(s) of {epSize}
        {nextEps === 0 && <span className="text-amber-700"> — raise the video pool target ("Set video pool target…" on the Question bank card) and run Repair bank to continue</span>}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <QuietBtn data-testid="start-video-job-btn" onClick={startVideos} disabled={nextEps === 0}>Render next {nextEps} episode{nextEps === 1 ? "" : "s"} ({epSize} questions each + 1 short each + full-set compilation)</QuietBtn>
        {videos.length > 0 && <QuietBtn danger data-testid="clear-videos-btn" onClick={clearVideos}>Delete all rendered videos</QuietBtn>}
        <label className="flex items-center gap-1.5 text-xs text-zinc-500">
          <input type="checkbox" data-testid="allow-mock-tts-checkbox" checked={allowMock} onChange={(e) => setAllowMock(e.target.checked)} />
          Dev preview: allow placeholder audio
        </label>
      </div>
      <details className="mt-3 text-xs text-zinc-500">
        <summary className="cursor-pointer">Voice: {ttsCfg?.config?.provider} / {ttsCfg?.config?.voice} — change or compare samples</summary>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="tts-config-box">
          {(ttsCfg?.candidates || []).map((c) => (
            <button key={`${c.provider}-${c.voice}`} data-testid={`tts-pick-${c.provider}-${c.voice}`}
                    onClick={() => saveTts(c.provider, c.voice)} disabled={!c.available}
                    className={`px-2.5 py-1 rounded-md border text-xs transition-colors duration-150 ${ttsCfg?.config?.provider === c.provider && ttsCfg?.config?.voice === c.voice ? "border-sky-500 bg-sky-50 text-sky-700" : "border-zinc-300 hover:border-sky-400"} disabled:opacity-40`}>
              {c.label}{!c.available && " (no key)"}
            </button>
          ))}
          <QuietBtn data-testid="render-voice-samples-btn" onClick={renderSamples}>Render voice comparison samples</QuietBtn>
        </div>
        <VoiceSamples />
      </details>

      <div className="mt-4 pt-4 border-t border-zinc-100">
        <label className="block text-xs font-medium text-zinc-500 max-w-md">Ticker URL
          <div className="mt-1 flex gap-2">
            <input data-testid="ticker-url-input" className={input} value={ticker} onChange={(e) => setTicker(e.target.value)} />
            <QuietBtn data-testid="ticker-url-save" onClick={saveTicker}>Save</QuietBtn>
          </div>
        </label>
        <p className="mt-1 text-xs text-zinc-400">Shown in every video for this cert.</p>
      </div>
    </Card>
  );
}

function VoiceSamples() {
  const [rendered, setRendered] = useState([]);
  useEffect(() => {
    api.get("/admin/voice-samples").then((r) => setRendered(r.data.rendered || [])).catch(() => {});
  }, []);
  if (!rendered.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="voice-samples-list">
      {rendered.map((s) => (
        <a key={s.id} href={`${API_BASE}/admin/voice-samples/${s.id}/download`} data-testid={`voice-sample-download-${s.id}`}
           className="text-xs px-2 py-1 border border-zinc-300 rounded-md text-sky-600 hover:underline">⬇ {s.label}</a>
      ))}
    </div>
  );
}

/* ---------------- secondary tabs (spot review, marketing, logs) ---------------- */

function QuestionRow({ q, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(q);
  const save = async () => {
    try {
      await api.put(`/admin/questions/${q.id}`, { stem: draft.stem, options: draft.options, correct_index: draft.correct_index });
      toast.success("Saved"); setEditing(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const clearFlags = async () => { await api.post(`/admin/questions/${q.id}/clear-flags`); toast.success("Flags cleared"); onSaved(); };
  return (
    <div className="border border-zinc-200 rounded-md p-4 bg-white" data-testid={`review-q-${q.id}`}>
      <div className="flex flex-wrap gap-2 items-center text-xs font-mono text-zinc-500">
        <span>{q.bank || "unassigned"}{q.exam_number ? ` · exam ${q.exam_number} q${q.question_number}` : ""}</span>
        <span>· {q.domain} · {q.objective_id}</span>
        {q.originality_score != null && <span title="Overlap vs source material and known banks — lower is better">· similarity {q.originality_score} (lower is better)</span>}
        {(q.flags || []).map((f) => <span key={f} className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{f}</span>)}
      </div>
      {editing ? (
        <div className="mt-3 space-y-2">
          <textarea className={input} rows={3} value={draft.stem} onChange={(e) => setDraft({ ...draft, stem: e.target.value })} data-testid="edit-stem" />
          {draft.options.map((o, i) => (
            <div key={`${q.id}-e-${i}`} className="flex gap-2 items-center">
              <input type="radio" checked={draft.correct_index === i} onChange={() => setDraft({ ...draft, correct_index: i })} data-testid={`edit-correct-${i}`} />
              <input className={input} value={o} onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, j) => j === i ? e.target.value : x) })} data-testid={`edit-option-${i}`} />
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="edit-save-btn" onClick={save} className="px-3 py-1.5 rounded-md bg-sky-500 text-white text-xs font-semibold">Save</button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm leading-relaxed">{q.stem}</p>
          <ol className="mt-2 space-y-1 text-sm">
            {q.options.map((o, i) => (
              <li key={`${q.id}-${i}`} className={i === q.correct_index ? "text-emerald-700 font-medium" : "text-zinc-600"}>
                <span className="font-mono text-xs mr-1">{"ABCD"[i]}</span>{o}
              </li>
            ))}
          </ol>
          <details className="mt-2 text-xs text-zinc-500"><summary className="cursor-pointer">Citations (admin-only) + explanations</summary>
            <p className="mt-1 font-mono">{(q.citations || []).map((c) => `${c.source} ${c.ref}: ${c.note}`).join(" | ")}</p>
            {Object.entries(q.explanations || {}).map(([k, v]) => <p key={k} className="mt-1">{"ABCD"[k]}: {v}</p>)}
          </details>
          <div className="mt-3 flex gap-2">
            <button data-testid={`edit-btn-${q.id}`} onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold hover:border-sky-400 transition-colors duration-150">Edit inline</button>
            {(q.flags || []).length > 0 && <button data-testid={`clear-flags-${q.id}`} onClick={clearFlags} className="px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 text-xs font-semibold">Clear flags</button>}
          </div>
        </>
      )}
    </div>
  );
}

export function ReviewQueueTab({ id }) {
  const [questions, setQuestions] = useState([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const loadQs = useCallback(() => {
    api.get(`/admin/certs/${id}/questions`, { params: { flagged: flaggedOnly, limit: 50 } })
      .then((r) => setQuestions(r.data.questions))
      .catch((e) => toast.error(errMsg(e)));
  }, [id, flaggedOnly]);
  useEffect(() => { loadQs(); }, [loadQs]);
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="flagged-only-toggle" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} /> Flagged only</label>
      {questions.map((q) => <QuestionRow key={q.id} q={q} onSaved={loadQs} />)}
      {questions.length === 0 && <p className="text-zinc-400 text-sm">No questions match.</p>}
    </div>
  );
}

export function VerificationLogTab({ id }) {
  const [vlog, setVlog] = useState([]);
  useEffect(() => {
    api.get(`/admin/verification-log`, { params: { cert_id: id } }).then((r) => setVlog(r.data.log))
      .catch((e) => toast.error(errMsg(e)));
  }, [id]);
  return (
    <div className="bg-white border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-[32rem] overflow-auto" data-testid="verification-log">
      {vlog.map((l) => (
        <div key={l.id} className="px-4 py-2 text-xs font-mono flex gap-3">
          <span className="text-zinc-400">{l.at?.slice(0, 19)}</span>
          <span>{l.objective_id}</span>
          <span className={l.result.startsWith("validated") ? "text-emerald-600" : "text-amber-600"}>{l.result}</span>
          <span className="text-zinc-500">{(l.supporting || []).map((s) => s.filename).join(", ") || "no support"}</span>
          {l.reverify && <span className="text-sky-600">reverify {l.version_tag}</span>}
        </div>
      ))}
      {vlog.length === 0 && <p className="px-4 py-6 text-zinc-400 text-sm">No verification entries.</p>}
    </div>
  );
}

export function EcoUpdateTab({ id, cert, onChanged }) {
  const [newVersion, setNewVersion] = useState("");
  const [ecoSource, setEcoSource] = useState("");
  const bumpVersion = async () => {
    try {
      if (ecoSource.length >= 20) {
        await api.post(`/admin/certs/${id}/sources`, { filename: `eco_${newVersion}.txt`, kind: "eco", text: ecoSource });
      }
      await api.post(`/admin/certs/${id}/version`, { version_tag: newVersion });
      toast.success("ECO re-verification job queued — flags will surface on the dashboard");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6 max-w-2xl space-y-3" data-testid="eco-update-box">
      <p className="text-sm text-zinc-600">Bump the cert version when the vendor updates the ECO. Optionally paste the updated ECO source; all affected objectives are re-validated and questions that no longer hold are flagged on the dashboard.</p>
      <input data-testid="eco-version-input" className={input} placeholder={`New version tag (current: ${cert.version_tag})`} value={newVersion} onChange={(e) => setNewVersion(e.target.value)} />
      <textarea data-testid="eco-source-input" rows={5} className={input} placeholder="Updated ECO source text (optional)" value={ecoSource} onChange={(e) => setEcoSource(e.target.value)} />
      <button data-testid="eco-reverify-btn" onClick={bumpVersion} disabled={!newVersion}
              className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150 disabled:opacity-50">
        Bump version & re-verify
      </button>
    </div>
  );
}

export function EditCertTab({ id, onChanged }) {
  const [cert, setCert] = useState(null);
  const [form, setForm] = useState(null);
  const [ecoJson, setEcoJson] = useState("");
  const [ecoNew, setEcoNew] = useState(null);
  const [covKey, setCovKey] = useState(0);
  const [newSrc, setNewSrc] = useState({ filename: "", kind: "study_guide", text: "" });
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/certs/${id}`);
      setCert(data);
      setForm({ name: data.name, vendor: data.vendor, style_profile: data.style_profile, exam_code: data.exam_code || "", short_name: data.short_name || "", certifying_body_url: data.certifying_body_url || "",
                version_tag: data.version_tag, price_usd: data.price_usd, ticker_url: data.ticker_url || "",
                ...data.exam_params, facts: { ...emptyExamFacts(), ...(data.exam_facts || {}) } });
      setEcoJson(JSON.stringify(data.eco.domains, null, 1));
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!cert || !form) return <p className="text-zinc-500 text-sm">Loading…</p>;
  const NUMS = [["num_paid_exams", "Paid exams"], ["questions_per_exam", "Questions / exam"], ["free_count", "Free-50 size"], ["udemy_exams", "Udemy exams"], ["udemy_questions", "Questions / Udemy exam"], ["duration_min", "Duration (min)"], ["video_questions", "Video pool (questions)"], ["video_episode_size", "Questions / video episode"]];
  const input = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400";

  const saveDetails = async () => {
    try {
      const exam_params = {};
      NUMS.forEach(([k]) => { exam_params[k] = +form[k]; });
      const ferrs = examFactErrors(form.facts);
      if (!form.exam_code.trim()) ferrs.unshift("Exam code is required (the vendor's exam ID, e.g. SY0-701)");
      if (ferrs.length) return toast.error("Exam facts: " + ferrs.join(" · "), { duration: 10000 });
      await api.patch(`/admin/certs/${id}`, {
        name: form.name, vendor: form.vendor, style_profile: form.style_profile, exam_code: form.exam_code.trim(), short_name: form.short_name.trim() || null, certifying_body_url: form.certifying_body_url.trim(),
        version_tag: form.version_tag, price_usd: +form.price_usd, ticker_url: form.ticker_url, exam_params,
        exam_facts: examFactsPayload(form.facts) });
      toast.success("Cert updated — regenerate to apply to the bank");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveEco = async () => {
    try {
      let domains;
      try { domains = JSON.parse(ecoJson); } catch { return toast.error("ECO JSON is invalid"); }
      await api.patch(`/admin/certs/${id}`, { eco_domains: domains });
      toast.success("ECO replaced — regenerate to rebuild against it");
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteSource = async (sid) => {
    try {
      await api.delete(`/admin/certs/${id}/sources/${sid}`);
      toast.success("Source removed");
      setCovKey((k) => k + 1);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addPastedSource = async () => {
    if (newSrc.text.length < 20) return toast.error("Paste at least 20 characters");
    try {
      await api.post(`/admin/certs/${id}/sources`, newSrc);
      setNewSrc({ filename: "", kind: "study_guide", text: "" });
      toast.success("Source added");
      setCovKey((k) => k + 1);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="edit-cert-tab" id="cert-edit">
      <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-3">
        <p className="text-sm font-bold">Cert details</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-medium text-zinc-600">Name
            <input data-testid="edit-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Vendor
            <input data-testid="edit-vendor" className={input} value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Style profile
            <select data-testid="edit-style" className={input} value={form.style_profile} onChange={(e) => setForm({ ...form, style_profile: e.target.value })}>
              <option value="comptia_direct">CompTIA — direct</option>
              <option value="pmi_nuanced">PMI — nuanced</option>
              <option value="isc2_managerial">ISC² — managerial</option>
              <option value="vendor_cloud">Vendor cloud — pragmatic</option>
            </select></label>
          <label className="text-xs font-medium text-zinc-600">ECO version tag
            <input data-testid="edit-version" className={input} value={form.version_tag} onChange={(e) => setForm({ ...form, version_tag: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Exam code <span className="text-red-600">*</span>
            <input data-testid="edit-exam-code" className={input} placeholder="SY0-701" value={form.exam_code} onChange={(e) => setForm({ ...form, exam_code: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Short name (video titles, spec sheet)
            <input data-testid="edit-short-name" className={input} placeholder="Security+" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Certifying body URL (How to qualify link)
            <input data-testid="edit-certifying-body-url" className={input} placeholder="https://www.comptia.org/certifications/security" value={form.certifying_body_url} onChange={(e) => setForm({ ...form, certifying_body_url: e.target.value })} /></label>
          <label className="text-xs font-medium text-zinc-600">Price (USD)
            <input data-testid="edit-price" type="number" className={input} value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} /></label>
          <div className="col-span-2" data-testid="discount-codes-note">
            <p className="text-xs font-bold text-zinc-600 mb-1">Discount codes</p>
            <p className="text-xs text-zinc-500">Codes are machine-wide and apply to every cert unless scoped. Manage them on the <Link to="/admin" className="text-teal-700 font-semibold hover:underline">Admin home</Link>; checkout, drip emails and video tickers render whatever is configured there.</p>
          </div>
          <label className="text-xs font-medium text-zinc-600">Ticker URL
            <input data-testid="edit-ticker" className={input} value={form.ticker_url} onChange={(e) => setForm({ ...form, ticker_url: e.target.value })} /></label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {NUMS.map(([k, l]) => (
            <label key={k} className="text-xs font-medium text-zinc-600">{l}
              <input data-testid={`edit-${k}`} type="number" className={input} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
          ))}
        </div>
        <ExamFactsFields facts={form.facts} onChange={(f) => setForm({ ...form, facts: f })} prefix="edit-facts" />
        <button data-testid="edit-save-btn" onClick={saveDetails} className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150">Save details</button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-3">
        <p className="text-sm font-bold">Sources <span className="text-xs font-normal text-zinc-400">— add, remove or replace at any time; regenerate runs against the current set</span></p>
        <ul className="text-sm font-mono text-zinc-600 space-y-1" data-testid="edit-src-list">
          {cert.sources.map((s) => (
            <li key={s.id} className="flex items-center gap-2">
              <span>{s.filename} ({s.kind} · {(s.chars || 0).toLocaleString()} chars)</span>
              <button data-testid={`edit-src-delete-${s.id}`} onClick={() => deleteSource(s.id)}
                      className="text-xs text-red-500 hover:underline">remove</button>
            </li>
          ))}
          {!cert.sources.length && <li className="text-zinc-400">No sources — upload the ECO and at least one study source.</li>}
        </ul>
        <div className="flex items-center gap-3">
          <select data-testid="edit-src-kind" className="border border-zinc-300 rounded-md px-2 py-1.5 text-sm bg-white" value={newSrc.kind} onChange={(e) => setNewSrc({ ...newSrc, kind: e.target.value })}>
            <option value="eco">ECO (official exam outline)</option>
            <option value="study_guide">Study guide / book</option>
            <option value="other">Other reference</option>
          </select>
          <label className="text-xs font-semibold text-sky-600 cursor-pointer hover:underline">
            {uploading !== null ? `Uploading… ${uploading}%` : "Upload file (PDF/DOCX/TXT/MD/CSV)"}
            <input data-testid="edit-src-file" type="file" accept=".pdf,.docx,.txt,.md,.csv" className="hidden" disabled={uploading !== null}
                   onChange={async (e) => {
                     const f = e.target.files?.[0];
                     if (!f) return;
                     setUploading(0);
                     try {
                       await uploadSourceChunked(id, f, newSrc.kind, setUploading);
                       toast.success("Source uploaded");
                       setCovKey((k) => k + 1);
                       load();
                     } catch (err) { toast.error(errMsg(err)); }
                     setUploading(null);
                     e.target.value = "";
                   }} />
          </label>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700">Paste source text instead</summary>
          <div className="mt-2 space-y-2">
            <input data-testid="edit-src-filename" className={input} placeholder="filename.txt" value={newSrc.filename} onChange={(e) => setNewSrc({ ...newSrc, filename: e.target.value })} />
            <textarea data-testid="edit-src-text" rows={4} className={input} placeholder="Source text (min 20 characters)" value={newSrc.text} onChange={(e) => setNewSrc({ ...newSrc, text: e.target.value })} />
            <button data-testid="edit-src-add-btn" onClick={addPastedSource} className="px-3 py-1.5 rounded-md border border-zinc-300 text-sm font-semibold hover:border-sky-400">+ Add pasted source</button>
          </div>
        </details>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-3">
        <p className="text-sm font-bold">ECO — master filter <span className="text-xs font-normal text-zinc-400">— replace by uploading the vendor's document; the machine extracts, you confirm</span></p>
        <EcoUploadPanel onExtracted={(data) => { setEcoNew(data); setEcoJson(JSON.stringify(data.domains, null, 1)); }} />
        {ecoNew && (
          <p className="text-sm font-semibold text-emerald-700" data-testid="edit-eco-summary">✓ {ecoNew.domains.length} domains, {ecoNew.objective_count} objectives found ({ecoNew.filename} · {ecoNew.chars_received.toLocaleString()} chars)</p>
        )}
        <EcoTree domains={(ecoNew && ecoNew.domains) || cert.eco.domains} />
        <details className="text-sm">
          <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-700">Advanced: edit the objectives JSON</summary>
          <textarea data-testid="edit-eco-json" rows={8} className={`${input} font-mono text-xs mt-2`} value={ecoJson} onChange={(e) => setEcoJson(e.target.value)} />
        </details>
        <button data-testid="edit-eco-save-btn" onClick={saveEco} className="px-4 py-2 rounded-md bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors duration-150">Save ECO</button>
      </div>

      <CoveragePanel certId={id} refreshKey={covKey} />
    </div>
  );
}


/* ---------------- page ---------------- */

export default function CertDetail() {
  return <CertFlow />;
}
